import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import { decode } from 'he'
import { Slugger } from 'marked'

import * as Components from './components'

/** Compute Heading component‘s slug for URL anchoring */
function getHeading(level = 'h2', children) {
    const [title] = children
    const slug = title ? new Slugger().slug(decode(title)) : null
    return (
        <Components.Heading level={level} slug={slug}>
            {children}
        </Components.Heading>
    )
}

/** Transform Contentful rich text contents into React components. */
export default function renderRichText(richText) {
    const json = richText.json
    const links = richText.links
    const options = {
        renderNode: {
            [BLOCKS.QUOTE]: (_, children) => (
                <Components.Blockquote>{children}</Components.Blockquote>
            ),
            [BLOCKS.HEADING_1]: (_, children) => getHeading('h1', children),
            [BLOCKS.HEADING_2]: (_, children) => getHeading('h2', children),
            [BLOCKS.HEADING_3]: (_, children) => getHeading('h3', children),
            [BLOCKS.HEADING_4]: (_, children) => getHeading('h4', children),
            [BLOCKS.HEADING_5]: (_, children) => getHeading('h5', children),
            [BLOCKS.HEADING_6]: (_, children) => getHeading('h6', children),
            [BLOCKS.EMBEDDED_ASSET]: (node) => {
                const assetId = node.data.target.sys.id
                const assetData = links.assets.block.find((entry) => entry.sys.id === assetId)

                if (!assetData) {
                    if (process.env.APP_ENV === 'development') {
                        console.log('Unsupported asset id', assetId)
                    }
                    return null
                }

                return (
                    <Components.Figure
                        caption={assetData.description}
                        imageUrl={assetData.url}
                        imageWidth={assetData.width}
                        imageHeight={assetData.height}
                    />
                )
            },
            [BLOCKS.EMBEDDED_ENTRY]: (node) => {
                /** Handle custom content types */
                const blockId = node.data.target.sys.id
                const blockData = links.entries.block.find((entry) => entry.sys.id === blockId)

                if (!blockData) {
                    if (process.env.APP_ENV === 'development') {
                        console.log('Unsupported content id', blockId)
                    }
                    return null
                }

                const blockType = blockData.__typename

                if (blockType === 'BlogExpertBox') {
                    return (
                        <Components.ExpertBox
                            name={blockData.expert.name}
                            description={blockData.expert.description}
                            avatarUrl={blockData.expert.avatar.url}
                            avatarAlt={blockData.expert.avatar.alt}
                            content={blockData.content}
                        />
                    )
                }

                if (blockType === 'BlogImageHeading') {
                    return (
                        <Components.ImageHeading
                            level={blockData.level.toLowerCase()}
                            title={blockData.title}
                            imageUrl={blockData.image.url}
                            imageAlt={blockData.image.alt}
                        />
                    )
                }

                if (blockType === 'ArticleTable') {
                    return <Components.Table columns={blockData.columnsCollection.items} />
                }

                if (blockType === 'BlogTip') {
                    return (
                        <Components.ProductTip
                            title={blockData.title}
                            htmlContent={blockData.content}
                        />
                    )
                }

                if (process.env.APP_ENV === 'development') {
                    console.log(blockData.__typename, 'content type not implemented')
                }

                return null
            },
        },
    }
    return documentToReactComponents(json, options)
}
